import { Button } from "@/components/ui/button";
import { useState, useRef, useMemo } from "react";
import { Link } from "react-router-dom";
import { Leaf, Check, Search, X, BookOpen, ArrowRight, Sparkles, FileText, AlertTriangle, Pill } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import ebookMockup from "@/assets/ebook-mockup.jpg";
import { remedyData, aliases, ratgeberMapping, sanitizeInput, AilmentData } from "@/data/remedyData";

// Popular search terms for chips
const popularSearches = [
  "Kopfschmerzen",
  "Erkältung", 
  "Schlafprobleme",
  "Verdauung",
  "Stress",
  "Gelenkschmerzen"
];

const HeroSection = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [isFocused, setIsFocused] = useState(false);
  const [selectedResult, setSelectedResult] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const isMobile = useIsMobile();

  // Get suggestions for autocomplete
  const suggestions = useMemo(() => {
    const sanitized = sanitizeInput(searchQuery);
    if (sanitized.length < 2) return [];

    const aliasMatch = aliases[sanitized];
    if (aliasMatch && remedyData[aliasMatch]) {
      return [aliasMatch];
    }

    const partialAliasMatches = Object.entries(aliases)
      .filter(([alias]) => alias.includes(sanitized) || sanitized.includes(alias.slice(0, 4)))
      .map(([, target]) => target);

    const directMatches = Object.keys(remedyData).filter(
      (key) => key.includes(sanitized) || sanitized.includes(key.slice(0, 4))
    );

    const combined = [...new Set([...partialAliasMatches, ...directMatches])];
    return combined.slice(0, 5);
  }, [searchQuery]);

  const handleSearch = (query: string) => {
    const sanitized = sanitizeInput(query);
    const aliasMatch = aliases[sanitized];
    const key = aliasMatch || sanitized;
    
    if (remedyData[key]) {
      setSelectedResult(key);
      setSearchQuery(key.charAt(0).toUpperCase() + key.slice(1));
      setIsFocused(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      handleSearch(searchQuery);
    }
  };

  const handleChipClick = (term: string) => {
    setSearchQuery(term);
    handleSearch(term);
  };

  const handleBuyClick = () => {
    window.open("https://payhip.com/b/NWTP2", "_blank");
  };

  const handleLearnMore = () => {
    const nextSection = document.querySelector('section:nth-of-type(2)');
    if (nextSection) {
      nextSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const clearSearch = () => {
    setSearchQuery("");
    setSelectedResult(null);
    inputRef.current?.focus();
  };

  const result: AilmentData | null = selectedResult ? remedyData[selectedResult] : null;

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-16 pb-12">
      {/* Background */}
      <div className="absolute inset-0 gradient-mesh opacity-50" />
      <div className="absolute inset-0 bg-[linear-gradient(to_right,hsl(var(--border))_1px,transparent_1px),linear-gradient(to_bottom,hsl(var(--border))_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_80%_80%_at_50%_50%,#000_70%,transparent_110%)] opacity-15" />
      
      {/* Decorative gradient orb */}
      <div 
        className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full blur-3xl opacity-10 pointer-events-none"
        style={{ background: 'radial-gradient(circle, hsl(var(--primary)) 0%, transparent 70%)' }}
      />

      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-4xl mx-auto text-center">
          
          {/* Premium Glass Badge */}
          <div className="relative inline-block mb-8 animate-fade-in-up group">
            {/* Subtle static glow */}
            <div className="absolute -inset-0.5 bg-primary/20 rounded-full blur-md opacity-50 group-hover:opacity-70 transition-opacity duration-300" />
            
            {/* Glass Container */}
            <div className="relative inline-flex items-center gap-3 px-6 py-3 bg-white/10 backdrop-blur-xl rounded-full border border-white/20 shadow-lg group-hover:bg-white/15 transition-colors duration-300">
              {/* Inner glass highlight */}
              <div className="absolute inset-x-4 top-0 h-px bg-gradient-to-r from-transparent via-white/40 to-transparent" />
              
              {/* Icon */}
              <div className="w-8 h-8 bg-primary/20 backdrop-blur-sm rounded-full flex items-center justify-center border border-primary/30">
                <Leaf className="w-4 h-4 text-primary" strokeWidth={2.5} />
              </div>
              
              {/* Text */}
              <span className="text-base font-semibold text-foreground/90">
                600+ Seiten Naturwissen
              </span>
            </div>
          </div>

          {/* Headline */}
          <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-semibold text-foreground leading-tight tracking-tight mb-4 animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
            Welche Beschwerden hast du?
          </h1>
          
          {/* Subtext */}
          <p className="text-lg sm:text-xl text-muted-foreground mb-8 animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
            Finde sofort das richtige Naturmittel – aus über 150 wissenschaftlich fundierten Heilmitteln
          </p>

          {/* Search Bar */}
          <form onSubmit={handleSubmit} className="relative max-w-2xl mx-auto mb-6 animate-fade-in-up" style={{ animationDelay: '0.3s' }}>
            <div className={`relative flex items-center bg-card/90 backdrop-blur-xl rounded-2xl border-2 transition-all duration-300 shadow-lg ${isFocused ? 'border-primary shadow-primary/20' : 'border-primary/20'}`}>
              <Search className="absolute left-5 w-6 h-6 text-primary" />
              <input
                ref={inputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  if (selectedResult) setSelectedResult(null);
                }}
                onFocus={() => setIsFocused(true)}
                onBlur={() => setTimeout(() => setIsFocused(false), 200)}
                placeholder="z.B. Kopfschmerzen, Erkältung, Schlafprobleme..."
                className="w-full pl-14 pr-14 py-5 bg-transparent text-foreground text-lg placeholder:text-muted-foreground focus:outline-none"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={clearSearch}
                  className="absolute right-16 p-1.5 rounded-full hover:bg-muted transition-colors"
                >
                  <X className="w-5 h-5 text-muted-foreground" />
                </button>
              )}
              <button
                type="submit"
                className="absolute right-3 p-2.5 bg-primary hover:bg-primary/90 rounded-xl transition-colors"
              >
                <ArrowRight className="w-5 h-5 text-primary-foreground" />
              </button>
            </div>

            {/* Autocomplete Suggestions */}
            {isFocused && suggestions.length > 0 && !selectedResult && (
              <div className="absolute top-full left-0 right-0 mt-2 bg-card/95 backdrop-blur-xl rounded-xl border border-primary/20 shadow-xl overflow-hidden z-50">
                {suggestions.map((suggestion) => (
                  <button
                    key={suggestion}
                    type="button"
                    onClick={() => handleSearch(suggestion)}
                    className="w-full px-5 py-3 text-left hover:bg-primary/10 transition-colors flex items-center gap-3 border-b border-border/50 last:border-0"
                  >
                    <Search className="w-4 h-4 text-muted-foreground" />
                    <span className="capitalize text-foreground">{suggestion}</span>
                    <span className="ml-auto text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full">
                      {remedyData[suggestion]?.category}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </form>

          {/* Popular Searches Chips - hide when result is shown */}
          {!selectedResult && (
            <div className="text-center mb-12 animate-fade-in-up" style={{ animationDelay: '0.4s' }}>
              <p className="text-sm text-muted-foreground mb-3">Beliebte Suchen</p>
              <div className="flex flex-wrap justify-center gap-2">
                {popularSearches.map((term) => (
                  <button
                    key={term}
                    onClick={() => handleChipClick(term)}
                    className="px-4 py-2 text-sm font-medium bg-card/80 hover:bg-primary/10 border border-border hover:border-primary/40 rounded-full shadow-sm transition-all duration-200 text-foreground hover:text-primary"
                  >
                    {term}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Search Result Card - shown when result exists */}
          {result && selectedResult && (
            <div className="max-w-2xl mx-auto mb-8 animate-fade-in-up">
              <div className="relative p-6 md:p-8 rounded-2xl bg-card/80 backdrop-blur-md border border-primary/20 shadow-[0_8px_30px_rgba(34,197,94,0.15)] text-left">
                {/* Success Header */}
                <div className="flex items-start gap-3 mb-5">
                  <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                    <Check className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="text-xl md:text-2xl font-semibold capitalize text-foreground">
                      {selectedResult}
                    </h3>
                    <span className="text-sm text-primary font-medium">{result.category}</span>
                  </div>
                </div>

                {/* Description */}
                <p className="text-muted-foreground mb-5 leading-relaxed">
                  {result.description}
                </p>

                {/* Application Forms */}
                {result.applicationForms && result.applicationForms.length > 0 && (
                  <div className="flex items-center gap-2 flex-wrap mb-5">
                    <Pill className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">Anwendung:</span>
                    {result.applicationForms.map((form) => (
                      <span
                        key={form}
                        className="px-2.5 py-1 text-xs font-medium bg-secondary/50 text-secondary-foreground rounded-full border border-border/50"
                      >
                        {form}
                      </span>
                    ))}
                  </div>
                )}

                {/* Remedies with Benefits */}
                <div className="mb-5">
                  <div className="flex items-center gap-2 mb-3">
                    <Sparkles className="w-4 h-4 text-primary" />
                    <p className="text-sm font-semibold text-foreground">
                      {result.remedies.length} natürliche Lösungen im E-Book:
                    </p>
                  </div>
                  <div className="space-y-2">
                    {result.remedies.slice(0, 5).map((remedy) => (
                      <div
                        key={remedy.name}
                        className="flex items-start gap-3 p-3 rounded-lg bg-primary/5 border border-primary/10"
                      >
                        <div className="w-7 h-7 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                          <Leaf className="w-3.5 h-3.5 text-primary" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-foreground text-sm">{remedy.name}</p>
                          <p className="text-xs text-muted-foreground">→ {remedy.benefit}</p>
                        </div>
                      </div>
                    ))}
                    {result.remedies.length > 5 && (
                      <p className="text-sm text-primary font-medium pl-10">
                        + {result.remedies.length - 5} weitere im E-Book
                      </p>
                    )}
                  </div>
                </div>

                {/* When to See Doctor */}
                {result.whenToSeeDoctor && (
                  <div className="mb-5 p-4 rounded-lg bg-amber-500/10 border border-amber-500/30">
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-semibold text-foreground mb-1">Wann zum Arzt?</p>
                        <p className="text-sm text-muted-foreground leading-relaxed">
                          {result.whenToSeeDoctor}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Related Conditions */}
                {result.relatedConditions && result.relatedConditions.length > 0 && (
                  <div className="mb-5">
                    <p className="text-sm text-muted-foreground mb-2">Verwandte Themen:</p>
                    <div className="flex flex-wrap gap-2">
                      {result.relatedConditions.map((condition) => (
                        <button
                          key={condition}
                          onClick={() => handleSearch(condition)}
                          className="px-3 py-1.5 text-sm font-medium bg-card hover:bg-primary/10 border border-border hover:border-primary/30 rounded-full transition-all duration-200 capitalize text-foreground hover:text-primary"
                        >
                          {condition}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Book Reference */}
                <div className="flex items-center gap-2 text-sm text-muted-foreground mb-5 p-4 rounded-lg bg-muted/50 border border-border/50">
                  <BookOpen className="w-5 h-5 text-primary flex-shrink-0" />
                  <span>
                    <strong className="text-foreground">Dosierungen & Warnhinweise</strong> auf <strong className="text-primary">Seite {result.page}</strong>
                  </span>
                </div>

                {/* CTA Button */}
                <Button
                  onClick={handleBuyClick}
                  className="w-full py-6 text-base md:text-lg font-semibold bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 shadow-lg hover:shadow-primary/25 transition-all duration-300 group"
                >
                  Alle Details freischalten – 19,99€
                  <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Button>

                {/* Trust Note */}
                <p className="text-center text-xs text-muted-foreground mt-4">
                  Sofortiger Download • 600+ Seiten • 150+ Naturmittel
                </p>

                {/* Ratgeber Link */}
                {ratgeberMapping[selectedResult.toLowerCase()] && (
                  <Link
                    to={`/ratgeber/${ratgeberMapping[selectedResult.toLowerCase()].slug}`}
                    className="mt-4 flex items-center justify-center gap-2 p-3 rounded-lg bg-primary/5 border border-primary/20 hover:bg-primary/10 transition-colors group"
                  >
                    <FileText className="w-4 h-4 text-primary" />
                    <span className="text-sm text-foreground">
                      <strong>Ausführlicher Ratgeber:</strong>{" "}
                      <span className="text-primary group-hover:underline">
                        {ratgeberMapping[selectedResult.toLowerCase()].title}
                      </span>
                    </span>
                    <ArrowRight className="w-4 h-4 text-primary group-hover:translate-x-1 transition-transform" />
                  </Link>
                )}
              </div>
            </div>
          )}

          {/* Product Section - Hidden when search result is shown */}
          {!selectedResult && (
            <div className="relative bg-card/70 backdrop-blur-xl rounded-3xl p-8 md:p-10 border border-primary/10 shadow-xl max-w-3xl mx-auto animate-fade-in-up transition-all duration-300 hover:translate-y-[-2px] hover:shadow-2xl hover:border-primary/20 group/card" style={{ animationDelay: '0.5s' }}>
              <div className="flex flex-col items-center text-center gap-6">
                
                {/* E-Book Mockup - Centered on top */}
                <div>
                  <img 
                    src={ebookMockup}
                    alt="Heilkraft der Natur E-Book"
                    className="w-72 md:w-96 h-auto rounded-xl shadow-lg transition-transform duration-300 group-hover/card:rotate-1"
                    loading="eager"
                  />
                </div>

                {/* Content - Below image */}
                <div>
                  <h2 className="text-2xl md:text-3xl font-serif font-semibold text-foreground mb-4">
                    Das komplette Naturheilkunde-Handbuch
                  </h2>
                  
                  {/* Benefits - Honest & Concrete */}
                  <div className="space-y-2 mb-5 text-left inline-block">
                    {[
                      "Exakte Dosierungen – auch für Kinder & Schwangere",
                      "50+ Beschwerden mit Sofort-Lösungen",
                      "Verständlich erklärt – ohne Fachchinesisch",
                      "Mit Warnhinweisen: Wann doch zum Arzt?"
                    ].map((benefit, i) => (
                      <div key={i} className="flex items-center gap-2.5">
                        <Check className="w-5 h-5 text-primary flex-shrink-0" strokeWidth={3} />
                        <span className="text-base md:text-lg text-foreground/80">{benefit}</span>
                      </div>
                    ))}
                  </div>

                  {/* CTA Buttons */}
                  <div className="flex flex-col sm:flex-row justify-center gap-3">
                    <Button
                      size="lg"
                      onClick={handleBuyClick}
                      className="group bg-primary hover:bg-primary/90 text-primary-foreground font-bold text-lg px-8 py-4 shadow-lg hover:shadow-xl transition-all duration-300 rounded-xl"
                    >
                      <span className="flex items-center gap-2">
                        <BookOpen className="w-5 h-5" />
                        Jetzt kaufen – 19,99€
                      </span>
                    </Button>
                    <Button
                      size="lg"
                      variant="outline"
                      onClick={handleLearnMore}
                      className="font-semibold text-lg px-8 py-4 rounded-xl border-2"
                    >
                      Mehr erfahren
                    </Button>
                  </div>

                  {/* Social Proof */}
                  <div className="flex items-center justify-center gap-4 mt-5 pt-5 border-t border-border/50">
                    <div className="flex items-center gap-1">
                      {[1, 2, 3, 4, 5].map((i) => (
                        <svg key={i} className="w-5 h-5 text-yellow-500 fill-current" viewBox="0 0 20 20">
                          <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                        </svg>
                      ))}
                      <span className="text-base text-muted-foreground ml-1">4.9/5</span>
                    </div>
                    <span className="text-base text-muted-foreground">•</span>
                    <span className="text-base font-medium text-foreground">500+ Downloads</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Trust Elements */}
          <div className="flex flex-wrap justify-center gap-4 md:gap-8 mt-8 text-sm text-muted-foreground animate-fade-in-up" style={{ animationDelay: '0.6s' }}>
            <span>💳 Sofort-Download</span>
            <span>🔒 SSL-Verschlüsselt</span>
            <span>⚡ Kein Abo</span>
          </div>

        </div>
      </div>
    </section>
  );
};

export default HeroSection;
